import React, { useMemo } from 'react';
import { Box, Paper, Typography } from '@mui/material';

// Import library functions and our hyper reactive cysteines data
import { Upset, process } from '@visdesignlab/upset2-react';
import { CoreUpsetData, TableRow, UpsetConfig, DefaultConfig } from '@visdesignlab/upset2-core';
import rawUpsetData from '../data/hyperReactiveCysteineData.json';

// Define the schema for the data
const TISSUE_NAMES = ['Breast', 'CNS', 'Colon', 'Leukemia', 'Melanoma', 'NSCL', 'Ovarian', 'Prostate', 'Renal'];
const annotations = {
  columns: {
    'Identifier': 'label' as const,
    'AltIdentifier': 'category' as const,
    ...Object.fromEntries(TISSUE_NAMES.map(name => [name, 'boolean' as const]))
  }
};

export default function HyperReactiveUpsetPlot() {
  // Process data and create config in a useMemo hook
  const { processedData, config } = useMemo(() => {
    // Convert imported JSON into the TableRow format for the 'process' function
    const tableRows: TableRow[] = rawUpsetData.map(item => ({ ...item, _key: item.id, _id: item.id, _rev: '' }));
    
    // Create the final CoreUpsetData object
    const data = process(tableRows, annotations.columns);

    // Manually build a complete, valid config object
    const newConfig: UpsetConfig = {
      ...DefaultConfig,
      version: '0.1.5',
      
      // Tell the component about ALL possible sets
      allSets: Object.values(data.sets).map(s => ({ name: s.id, size: s.size })),
      
      // Display all 9 sets for hyper reactive cysteines
      visibleSets: TISSUE_NAMES.map(name => `Set_${name}`),

      // Show only Degree column
      visibleAttributes: ['Degree'],
      plots: { scatterplots: [], histograms: [] },
    };
    
    return { processedData: data, config: newConfig };
  }, []);

  // Render the component
  return (
    <Paper sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h5" gutterBottom>
        Hyper Reactive Cysteine Distribution Across Cancer Tissues
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {rawUpsetData.length.toLocaleString()} hyper reactive cysteines
      </Typography>
      <Box sx={{ flexGrow: 1, height: '800px', width: '100%' }}>
        <Upset
          data={processedData}
          config={config}
          parentHasHeight={true}
        />
      </Box>
    </Paper>
  );
}